import * as ko from 'knockout'

import {
	HistoryLogsStore,
	IHistoryTableOption,
	IHistoryRecordOption,
	IHistoryOperation,
	IHistoryFieldChange
} from 'Core/Controls/History/Stores/HistoryLogsStore';
import { DATE_FORMATS } from 'Core/Constants/DateTimeFormats';
import { FormatConverter } from 'FormatEditor/FormatConverter'

import HistoryContentTemplate from "Core/Controls/History/Templates/HistoryContent.html"


ko.templates["Core/Controls/History/Templates/HistoryContent"] = HistoryContentTemplate;

export interface IHistoryLogsData {
	EntityId: number;
	RecordId: number;
	Tables: IHistoryTableOption[];
}

class HistoryFieldVm {
	FieldName: string;
	OldValue: string;
	NewValue: string;
	HasOld: boolean;
	HasNew: boolean;

	constructor(field: IHistoryFieldChange, formatDate: (value: string) => string) {
		const format = (value: string) =>
			field.Type === 'DateTime' && value ? formatDate(value) : value;

		this.FieldName = field.FieldName;
		this.OldValue = format(field.OldValue);
		this.NewValue = format(field.NewValue);
		this.HasOld = field.OldValue !== null && field.OldValue !== undefined && field.OldValue !== '';
		this.HasNew = field.NewValue !== null && field.NewValue !== undefined && field.NewValue !== '';
	}
}

class HistoryOperationVm {
	OperationId: string;
	FormattedDate: string;
	OperationName: string;
	OperationColor: string;
	IsCreate: boolean;
	IsDelete: boolean;
	ChangedBy: string;
	FieldCount: number;
	IsExpanded: KnockoutObservable<boolean>;
	Fields: HistoryFieldVm[];

	constructor(operation: IHistoryOperation, formatDate: (value: string) => string) {
		const kind = (operation.OperationKind || '').toLowerCase();
		const isCreate = kind === 'create';
		const isDelete = kind === 'delete';

		this.OperationId = operation.OperationId;
		this.FormattedDate = formatDate(operation.ChangeDate);
		this.OperationName = operation.OperationName;
		this.OperationColor = isCreate ? '#36c6d3' : isDelete ? '#e7505a' : '#F1C40F';
		this.IsCreate = isCreate;
		this.IsDelete = isDelete;
		this.ChangedBy = operation.ChangedBy;
		this.FieldCount = operation.FieldCount;
		this.IsExpanded = ko.observable(false);
		this.Fields = (operation.Fields || []).map(field => new HistoryFieldVm(field, formatDate));
	}

	ToggleExpand = (): void => {
		this.IsExpanded(!this.IsExpanded());
	};
}

export class HistoryLogs {
	private _entityId: number;
	private _recordId: number;
	private _options: IHistoryTableOption[];
	private _selectedTableId: KnockoutObservable<number>;
	private _records: KnockoutObservableArray<IHistoryRecordOption>;
	private _recordOptions: KnockoutComputed<IHistoryRecordOption[]>;
	private _selectedRecordId: KnockoutObservable<number>;
	private _skipRecordReload: boolean;
	private _operations: KnockoutObservableArray<HistoryOperationVm>;
	private _isLoading: KnockoutObservable<boolean>;
	private _error: KnockoutObservable<string>;
	private _hasLoaded: KnockoutObservable<boolean>;

	constructor(data: IHistoryLogsData) {
		this._entityId = data.EntityId;
		this._recordId = data.RecordId;
		this._selectedTableId = ko.observable(null);
		this._records = ko.observableArray([]);
		this._selectedRecordId = ko.observable(0);
		this._skipRecordReload = false;
		this._operations = ko.observableArray([]);
		this._isLoading = ko.observable(false);
		this._error = ko.observable('');
		this._hasLoaded = ko.observable(false);

		const tables = data.Tables || [];
		this._options = tables;

		// A record picker is only useful when the table holds more than one of the subject's records.
		this._recordOptions = ko.computed(() => {
			const records = this._records();
			if (records.length <= 1) return [];
			return [{ RecordId: 0, Name: 'All records' } as IHistoryRecordOption].concat(records);
		});

		this._selectedTableId.subscribe(tableId => {
			if (tableId === null || tableId === undefined) return;
			// Reset the record picker to "All" without triggering a second history load.
			this._skipRecordReload = true;
			this._selectedRecordId(0);
			this._skipRecordReload = false;

			this.RefreshRecords(tableId);
			this.LoadHistory(tableId, 0);
		});

		this._selectedRecordId.subscribe(recordSelectionId => {
			if (this._skipRecordReload) return;
			this.LoadHistory(this._selectedTableId(), recordSelectionId);
		});

		const main = tables.filter(t => t.IsMain)[0] || tables[0];
		if (main) {
			this._selectedTableId(main.TableId);
		}
	}

	private RefreshRecords(tableId: number): void {
		this._records([]);

		HistoryLogsStore.GetTableRecords({ EntityId: this._entityId, RecordId: this._recordId, TableId: tableId })
			.then(result => {
				const records = (result && result.IsSuccessfull !== false && result.ResultObject) || [];
				this._records(records);
			})
			.fail(() => this._records([]));
	}

	private LoadHistory(tableId: number, recordSelectionId: number): void {
		this._isLoading(true);
		this._error('');
		this._hasLoaded(false);
		this._operations([]);

		HistoryLogsStore.GetTableHistory({
			EntityId: this._entityId,
			RecordId: this._recordId,
			TableId: tableId,
			RecordSelectionId: recordSelectionId
		})
			.then(result => {
				if (result && result.IsSuccessfull === false) {
					this._error(result.ErrorMessage || 'Failed to load history.');
					return;
				}
				const operations = ((result && result.ResultObject) || [])
					.map((operation: IHistoryOperation) => new HistoryOperationVm(operation, this.FormatDate));
				this._operations(operations);
				this._hasLoaded(true);
			})
			.fail(() => this._error('Failed to load history.'))
			.always(() => this._isLoading(false));
	}

	private FormatDate = (date: string): string => {
		if (!date) return '';
		return FormatConverter.ConvertFromDefaultFormat(
			FormatConverter.CorrectTimezone(date),
			DATE_FORMATS.DATE_TIME.MomentFormat
		);
	};

	GetTemplateName() {
		return 'Core/Controls/History/Templates/HistoryContent';
	}

	AfterRender() {
	}
}
